import * as Sentry from "@sentry/react";

/**
 * Browser page translation (Chrome/Google Translate, Edge, Safari) replaces
 * every text node it translates with a <font> element. React keeps pointing at
 * the original text node, which is now detached. The next time React removes
 * that node, or inserts a sibling in front of it, the DOM throws:
 *
 *   Failed to execute 'insertBefore' on 'Node': The node before which the new
 *   node is to be inserted is not a child of this node.
 *   Failed to execute 'removeChild' on 'Node': The node to be removed is not a
 *   child of this node.
 *
 * and the route error boundary takes over the page. It only happens with
 * translation on, so it never reproduces for us (support ticket #75).
 *
 * The lint rule `local/no-unwrapped-dynamic-text` catches the patterns it can
 * see in a single file; this guard is the safety net for everything else, e.g.
 * a text prop rendered by a component that toggles an icon next to it.
 * Instead of throwing, removeChild skips a node that is already gone, and
 * insertBefore appends when its reference node is gone: the new node lands in
 * the right parent, maybe a few words out of place, instead of the page dying.
 * The cost is that translated text may go stale after a re-render.
 *
 * See https://github.com/facebook/react/issues/11538.
 */

let reported = false;

/* Once per page load: a translated page can hit this on every render. */
const report = (method: "insertBefore" | "removeChild") => {
  if (reported) return;
  reported = true;
  Sentry.captureMessage(`Translation DOM guard: ${method} on detached node`, {
    level: "warning",
    tags: {
      report_kind: "translation-dom-guard",
      translation_dom_guard: method,
      translated: document.documentElement.className.includes("translated-"),
    },
    fingerprint: ["translation-dom-guard", method],
  });
};

export const installTranslationDomGuard = () => {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T) {
    if (child.parentNode !== this) {
      report("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ) {
    if (referenceNode && referenceNode.parentNode !== this) {
      report("insertBefore");
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
